import config from '@epi-config';
import { post } from '@epi-helpers/FetchHelpers';
import { reduxStore } from '../store-configuration';
import Moment from './MomentExtensions';
import { shouldRefreshToken } from './AuthorizeHelper';
import { ApiConstants } from '../constants/actions';
import storage from './StorageHelper';

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

interface StoredToken {
  refreshToken: string;
  accessToken: string;
  expiresIn: number;
  tokenType: string;
}

interface LoginState {
  login: {
    refreshingToken: boolean;
    refreshToken: string;
    validTo: number | null;
    expiresIn: number | string;
    isAuthenticated: boolean;
  };
}

async function tokenRefresher(): Promise<void> {
  const storedToken = storage.get('rememberMe') as StoredToken | null;
  const { login } = reduxStore.getState() as LoginState;
  const { refreshToken } = login;

  reduxStore.dispatch({ type: `${ApiConstants.REFRESH_TOKEN}_REQUEST` });
  try {
    const response = await post<TokenResponse>(
      `${config.apiHost}/api/login/refresh`,
      {
        refreshToken
      }
    );

    const newValidTo = Moment().unix() + response.expiresIn;
    const action = {
      type: `${ApiConstants.REFRESH_TOKEN}_SUCCESS`,
      payload: {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        validTo: newValidTo
      }
    };

    if (storedToken?.refreshToken)
      storage.set('rememberMe', {
        ...storedToken,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
        tokenType: response.tokenType
      });

    reduxStore.dispatch(action);
  } catch (ex: any) {
    if (ex.content?.error === 'invalid_grant') {
      const logoutAction = {
        type: `${ApiConstants.LOGOUT_ACTION}_REQUEST`,
        sessionExpired: true
      };
      reduxStore.dispatch(logoutAction);
    }
  }
}

export default function (refreshInterval: number): void {
  setInterval(async () => {
    const { login } = reduxStore.getState() as LoginState;
    const {
      refreshingToken,
      refreshToken,
      validTo,
      expiresIn,
      isAuthenticated
    } = login;

    if (refreshingToken || !refreshToken || !isAuthenticated) {
      return;
    }

    const shouldRefresh = shouldRefreshToken(validTo, Number(expiresIn));

    if (!shouldRefresh) {
      return;
    }

    tokenRefresher();
  }, refreshInterval);
}

export async function refreshRememberedUser() {
  const storedToken = storage.get('rememberMe');

  if (storedToken) {
    await tokenRefresher();
  }
}
